<script setup lang="ts">
import {computed} from "vue";
import type {ClockHand, Component} from "../types.ts";
import useStore from "../store.ts";
import {parsePx} from "../utils/PureElementUtils.ts";

const props = defineProps<{
  item: Component
}>();

const store = useStore();

/* see docs/EXTRACTED_COMMENTS.md#c778 */
const clock = props.item.analog_clock_props ?? {};

const plateBackground = clock.plate_color ?? "transparent";
const hourHandColor = clock.hour_hand?.color ?? "currentColor";
const minuteHandColor = clock.minute_hand?.color ?? "currentColor";
const secondHandColor = clock.second_hand?.color ?? "currentColor";
const hourMarkerColor = clock.hour_marker?.color ?? "currentColor";
const minuteMarkerColor = clock.minute_marker?.color ?? "currentColor";

// The declared frame box drives the viewBox, so every px dimension in the proto maps 1:1 onto a
// user unit and the face still scales with the element when the renderer resizes it.
const box = computed(() => {
  const style: any = props.item.props?.style ?? {};
  const width = parsePx(style.width) || 100;
  const height = parsePx(style.height) || 100;
  return {width, height, cx: width / 2, cy: height / 2, r: Math.min(width, height) / 2};
});

// The plate is an HTML element (an SVG fill cannot take a CSS gradient), sized in percentages of
// the frame box so it scales exactly like the viewBox-driven SVG drawn on top of it.
const plateStyle = computed(() => {
  const {width, height, cx, cy, r} = box.value;
  const pct = (value: number, total: number) => `${(value / total) * 100}%`;
  return {
    left: pct(cx - r, width),
    top: pct(cy - r, height),
    width: pct(2 * r, width),
    height: pct(2 * r, height),
  };
});

// Recomputes once per second, not once per 100ms store tick.
const nowSeconds = computed(() => Math.floor(store.currentDate / 1000));

const time = computed(() => {
  const date = new Date(nowSeconds.value * 1000);
  const timeZone = clock.time_zone;
  if (timeZone) {
    try {
      const parts = new Intl.DateTimeFormat("en-GB", {
        timeZone,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hourCycle: "h23",
      }).formatToParts(date);
      const part = (type: string) => Number(parts.find(p => p.type === type)?.value ?? 0);
      return {hours: part("hour"), minutes: part("minute"), seconds: part("second")};
    } catch (e) {
      console.warn(`[AnalogClock] ${props.item.props.id}: unusable time_zone "${timeZone}"`, e);
    }
  }
  return {hours: date.getHours(), minutes: date.getMinutes(), seconds: date.getSeconds()};
});

const angles = computed(() => {
  const {hours, minutes, seconds} = time.value;
  return {
    hour: (hours % 12) * 30 + minutes * 0.5,
    minute: minutes * 6 + seconds * 0.1,
    second: seconds * 6,
  };
});

// Absent block → not drawn. Present block with zero/missing px → conventional proportions (#c778).
const dimensions = (source: ClockHand | undefined, lengthRatio: number, thicknessRatio: number) => {
  if (!source) return null;
  const {r} = box.value;
  const length = Number(source.length) > 0 ? Number(source.length) : r * lengthRatio;
  const thickness = Number(source.thickness) > 0 ? Number(source.thickness) : Math.max(1, r * thicknessRatio);
  return {length, thickness};
};

const hourHand = computed(() => dimensions(clock.hour_hand, 0.5, 0.06));
const minuteHand = computed(() => dimensions(clock.minute_hand, 0.75, 0.045));
const secondHand = computed(() => dimensions(clock.second_hand, 0.85, 0.02));

const markers = (source: ClockHand | undefined, count: number, skipEvery: number | null) => {
  const dims = dimensions(source, 0.08, 0.03);
  if (!dims) return null;
  const ticks: number[] = [];
  for (let i = 0; i < count; i++) {
    if (skipEvery != null && i % skipEvery === 0) continue;
    ticks.push(i * (360 / count));
  }
  return {...dims, ticks};
};

const hourMarkers = computed(() => markers(clock.hour_marker, 12, null));
const minuteMarkers = computed(() => markers(clock.minute_marker, 60, clock.hour_marker ? 5 : null));
</script>

<template>
  <div v-bind="item.props">
    <div class="plate" :style="plateStyle"/>
    <svg
        class="face"
        :viewBox="`0 0 ${box.width} ${box.height}`"
        preserveAspectRatio="xMidYMid meet"
    >
      <line
          v-for="(angle, index) in (minuteMarkers?.ticks ?? [])"
          :key="`minute-marker-${index}`"
          class="minute-marker"
          :x1="box.cx"
          :y1="box.cy - box.r"
          :x2="box.cx"
          :y2="box.cy - box.r + minuteMarkers!.length"
          :stroke-width="minuteMarkers!.thickness"
          :transform="`rotate(${angle} ${box.cx} ${box.cy})`"
      />
      <line
          v-for="(angle, index) in (hourMarkers?.ticks ?? [])"
          :key="`hour-marker-${index}`"
          class="hour-marker"
          :x1="box.cx"
          :y1="box.cy - box.r"
          :x2="box.cx"
          :y2="box.cy - box.r + hourMarkers!.length"
          :stroke-width="hourMarkers!.thickness"
          :transform="`rotate(${angle} ${box.cx} ${box.cy})`"
      />

      <line
          v-if="hourHand"
          class="hand hour-hand"
          :x1="box.cx"
          :y1="box.cy"
          :x2="box.cx"
          :y2="box.cy - hourHand.length"
          :stroke-width="hourHand.thickness"
          :transform="`rotate(${angles.hour} ${box.cx} ${box.cy})`"
      />
      <line
          v-if="minuteHand"
          class="hand minute-hand"
          :x1="box.cx"
          :y1="box.cy"
          :x2="box.cx"
          :y2="box.cy - minuteHand.length"
          :stroke-width="minuteHand.thickness"
          :transform="`rotate(${angles.minute} ${box.cx} ${box.cy})`"
      />
      <line
          v-if="secondHand"
          class="hand second-hand"
          :x1="box.cx"
          :y1="box.cy"
          :x2="box.cx"
          :y2="box.cy - secondHand.length"
          :stroke-width="secondHand.thickness"
          :transform="`rotate(${angles.second} ${box.cx} ${box.cy})`"
      />
    </svg>
    <slot>
    </slot>
  </div>
</template>

<style scoped>
.plate {
  position: absolute;
  border-radius: 50%;
  background: v-bind(plateBackground);
}

.face {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
  overflow: visible;
}

.hand {
  stroke-linecap: round;
}

.hour-hand {
  stroke: v-bind(hourHandColor);
}

.minute-hand {
  stroke: v-bind(minuteHandColor);
}

.second-hand {
  stroke: v-bind(secondHandColor);
}

.hour-marker {
  stroke: v-bind(hourMarkerColor);
}

.minute-marker {
  stroke: v-bind(minuteMarkerColor);
}
</style>
